import { useMemo } from 'react';
import { isBigwaterConfigured } from '@/lib/api';
import { useAuthStore } from '@/store/authStore';
import { useParticipantProfile } from '@/hooks/useParticipantProfile';
import type { Quest } from '@/lib/types';

/**
 * Synthesizes the "Connect Telegram Bot" onboarding card as a `Quest`-shaped
 * object. Unlike every other quest, its data source is bigwater_backend
 * (rail2) rather than the `/v1/quests` catalogue, so it's built here and
 * merged into the onboarding section client-side (see
 * `mergeSyntheticOnboardingQuest` in `@/lib/adapters`) instead of coming
 * through `fetchQuests`.
 */
export function useTelegramBotQuest(): Quest | null {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const { data: participantProfile } = useParticipantProfile();

  return useMemo<Quest | null>(() => {
    if (!isBigwaterConfigured()) return null;

    return {
      id: 'connect-telegram-bot',
      slug: 'connect-telegram-bot',
      title: 'Connect Telegram Bot',
      description:
        'Connect the BigWater bot to submit proof of impact. Link your Telegram account to the bot to get instant updates.',
      category: 'onboarding',
      type: 'telegram_bot_connect',
      // Awarded server-side by bigwater_backend (TELEGRAM_BOT_CONNECT_POINTS
      // in src/services/identity.ts) the instant the bot link succeeds —
      // keep these two values in sync manually, there's no shared config.
      points: 100,
      status: !isAuthenticated
        ? 'locked'
        : participantProfile?.telegramId
          ? 'completed'
          : 'available',
      icon: 'bot',
      verifyMethod: 'auto',
      ctaLabel: 'Connect Bot',
    };
  }, [isAuthenticated, participantProfile?.telegramId]);
}
